import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { formatDateShort, formatDateLong } from '../../utils/dateFormatting';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { orderBlocksChronologically } from '../../lib/timeBlocks';
import { 
  ChevronLeft, 
  ChevronRight,
  Loader2,
  Clock,
  Calendar,
  Coffee,
  Check,
  Send,
  Home,
  Briefcase,
  FileText,
  Layers,
  Plus,
  Trash2,
  AlertTriangle
} from 'lucide-react';

const _pad = (n) => String(n).padStart(2, '0');
// Device-LOCAL date helpers — clock entries are stored with the device's local
// date, so the timesheet week/selection must use local dates too (not UTC),
// otherwise the just-clocked entry appears to be missing for non-UTC users.
const localDateStr = (d = new Date()) => `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}`;
const localWeekStart = () => {
  const d = new Date();
  const dow = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - dow);
  return localDateStr(d);
};

const MobileTimesheet = () => {
  const navigate = useNavigate();
  const { api, tenantSettings } = useMobileAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weekData, setWeekData] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [editForm, setEditForm] = useState({
    start_time: '',
    finish_time: '',
    lunch_break_minutes: 30,
    notes: '',
    time_blocks: [],
  });
  const [jobsForDate, setJobsForDate] = useState([]);
  const [autoFillLoading, setAutoFillLoading] = useState(false);

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Match MobileCreateDocket's time picker (dropdowns in 5-min steps)
  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  const minutes = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

  const updateTimePart = (field, part, value) => {
    const fallback = field === 'start_time' ? defaultStart : defaultFinish;
    const current = editForm[field] || fallback;
    const [h, m] = current.split(':');
    const next = part === 'hour' ? `${value}:${m || '00'}` : `${h || '00'}:${value}`;
    setEditForm((prev) => ({ ...prev, [field]: next }));
  };

  useEffect(() => {
    fetchTimesheet(localWeekStart());
  }, []);

  const fetchTimesheet = async (weekStart = null) => {
    setLoading(true);
    try {
      const url = weekStart 
        ? `/api/mobile/timesheets?week_start=${weekStart}`
        : '/api/mobile/timesheets';
      const response = await api.get(url);
      setWeekData(response.data);
      
      // Auto-select today if in current week (device-local date)
      const today = localDateStr();
      const todayEntry = response.data.entries.find(e => e.date === today);
      if (todayEntry) {
        selectDay(todayEntry);
      } else if (response.data.entries.length > 0) {
        selectDay(response.data.entries[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch timesheet');
    } finally {
      setLoading(false);
    }
  };

  const defaultStart = tenantSettings?.default_working_hours?.start || '07:00';
  const defaultFinish = tenantSettings?.default_working_hours?.end || '17:00';
  const defaultLunch = tenantSettings?.default_working_hours?.lunch_break_minutes ?? 30;

  const selectDay = (entry) => {
    setSelectedDay(entry);
    setEditForm({
      start_time: entry.start_time || defaultStart,
      finish_time: entry.finish_time || defaultFinish,
      lunch_break_minutes: entry.lunch_break_minutes ?? defaultLunch,
      notes: entry.notes || '',
      time_blocks: entry.time_blocks || [],
    });
    // Fetch jobs this worker is allocated to for this date (display only)
    api.get(`/api/mobile/timesheets/jobs-for-date?date=${entry.date}`)
      .then(res => setJobsForDate(res.data || []))
      .catch(() => setJobsForDate([]));
  };

  const navigateWeek = (direction) => {
    if (!weekData) return;
    const currentStart = new Date(weekData.week_start);
    currentStart.setDate(currentStart.getDate() + (direction * 7));
    const localDate = `${currentStart.getFullYear()}-${String(currentStart.getMonth() + 1).padStart(2, '0')}-${String(currentStart.getDate()).padStart(2, '0')}`;
    fetchTimesheet(localDate);
  };

  const handleSave = async () => {
    if (!selectedDay) return;
    
    if (editForm.start_time && editForm.finish_time && editForm.start_time === editForm.finish_time) {
      toast.error('Start and finish time cannot be the same');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        date: selectedDay.date,
        start_time: editForm.start_time || null,
        finish_time: editForm.finish_time || null,
        lunch_break_minutes: editForm.lunch_break_minutes,
        notes: editForm.notes || null,
      };
      if (editForm.time_blocks && editForm.time_blocks.length > 0) {
        payload.time_blocks = editForm.time_blocks;
      }
      const res = await api.post('/api/mobile/timesheets', payload);
      const warnings = res.data?.block_warnings;
      if (warnings?.length) {
        toast.warning(`Saved with ${warnings.length} overlap warning(s)`);
      } else {
        toast.success('Timesheet saved');
      }
      fetchTimesheet(weekData.week_start);
    } catch (error) {
      toast.error('Failed to save timesheet');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitWeek = async () => {
    if (!weekData) return;
    
    try {
      // Auto-save the currently selected day if it has times and is still draft
      if (selectedDay?.status === 'draft' && editForm.start_time && editForm.finish_time) {
        const payload = {
          date: selectedDay.date,
          start_time: editForm.start_time,
          finish_time: editForm.finish_time,
          lunch_break_minutes: editForm.lunch_break_minutes,
          notes: editForm.notes || null,
        };
        if (editForm.time_blocks && editForm.time_blocks.length > 0) {
          payload.time_blocks = editForm.time_blocks;
        }
        await api.post('/api/mobile/timesheets', payload);
      }

      await api.post(`/api/mobile/timesheets/submit-week?week_start=${weekData.week_start}`);
      toast.success('Timesheet submitted for approval');
      fetchTimesheet(weekData.week_start);
    } catch (error) {
      toast.error('Failed to submit timesheet');
    }
  };

  const handleAutoFillBlocks = async () => {
    if (!selectedDay) return;
    setAutoFillLoading(true);
    try {
      const params = new URLSearchParams({ operator_id: 'me', date: selectedDay.date });
      if (editForm.start_time) params.append('shift_start', editForm.start_time);
      if (editForm.finish_time) params.append('shift_end', editForm.finish_time);
      const res = await api.get(`/api/timesheets/auto-blocks?${params}`);
      const blocks = res.data.blocks || [];
      if (blocks.length === 0) {
        toast.info('No dockets found for this date');
        return;
      }
      setEditForm(prev => ({ ...prev, time_blocks: blocks }));
      toast.success(`Generated ${blocks.length} block(s) from ${res.data.docket_count} docket(s)`);
    } catch {
      toast.error('Failed to auto-fill blocks');
    } finally {
      setAutoFillLoading(false);
    }
  };

  const addBlock = () => {
    const lastBlock = editForm.time_blocks[editForm.time_blocks.length - 1];
    const newStart = lastBlock?.end_time || editForm.start_time || '07:00';
    setEditForm(prev => ({
      ...prev,
      time_blocks: [...prev.time_blocks, {
        block_id: `mob-${Date.now()}`,
        job_id: null, job_number: null, customer_name: '',
        start_time: newStart, end_time: editForm.finish_time || '17:00',
        break_minutes: 0, hours: 0, is_unallocated: true,
        tracking: {}, notes: '',
      }],
    }));
  };

  const removeBlock = (idx) => {
    setEditForm(prev => ({
      ...prev,
      time_blocks: prev.time_blocks.filter((_, i) => i !== idx),
    }));
  };

  const calcBlockHrs = (st, et, brk = 0) => {
    if (!st || !et) return 0;
    const [sh, sm] = st.split(':').map(Number);
    const [eh, em] = et.split(':').map(Number);
    let s = sh * 60 + sm, e = eh * 60 + em;
    if (e <= s) e += 24 * 60;
    return Math.max(0, (e - s - brk) / 60);
  };

  const getBlocksTotal = () => {
    return editForm.time_blocks.reduce((s, b) => s + calcBlockHrs(b.start_time, b.end_time, b.break_minutes || 0), 0).toFixed(1);
  };

  const mobileOverlaps = (() => {
    const bl = editForm.time_blocks;
    if (!bl || bl.length < 2) return { set: new Set(), msgs: [] };
    const ordered = orderBlocksChronologically(bl);
    const set = new Set();
    const msgs = [];
    for (let a = 0; a < ordered.length; a++) {
      for (let b = a + 1; b < ordered.length; b++) {
        if (ordered[b]._offset_start < ordered[a]._offset_end) {
          const origA = ordered[a]._orig_idx;
          const origB = ordered[b]._orig_idx;
          set.add(origA);
          set.add(origB);
          const mins = Math.min(ordered[a]._offset_end, ordered[b]._offset_end) - ordered[b]._offset_start;
          msgs.push(`Block ${origA + 1} overlaps Block ${origB + 1} by ${mins}min`);
        }
      }
    }
    return { set, msgs };
  })();

  const formatDate = (dateStr) => {
    const date = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00');
    return date.getDate();
  };

  const formatWeekRange = () => {
    if (!weekData) return '';
    return `${formatDateShort(weekData.week_start, tenantSettings?.date_format)} - ${formatDateShort(weekData.week_end, tenantSettings?.date_format)}`;
  };

  const calculateHours = () => {
    if (!editForm.start_time || !editForm.finish_time) return 0;
    const [sH, sM] = editForm.start_time.split(':').map(Number);
    const [fH, fM] = editForm.finish_time.split(':').map(Number);
    let startMin = sH * 60 + sM;
    let finishMin = fH * 60 + fM;
    if (finishMin <= startMin) finishMin += 24 * 60; // overnight shift
    const diff = (finishMin - startMin - editForm.lunch_break_minutes) / 60;
    return Math.max(0, diff).toFixed(1);
  };

  const getTotalWeekHours = () => {
    if (!weekData) return 0;
    return weekData.entries.reduce((sum, e) => sum + (e.total_hours || 0), 0).toFixed(1);
  };

  const isToday = (dateStr) => {
    return dateStr === localDateStr();
  };

  const getEntryStatus = (entry) => {
    if (entry.status === 'submitted') return 'submitted';
    if (entry.status === 'approved') return 'approved';
    if (entry.total_hours > 0) return 'filled';
    return 'empty';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--m-bg)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  return (
    <MobileLayout>
    <div className="min-h-full bg-[var(--m-bg)] pb-6">
      {/* Header */}
      <div className="bg-[var(--m-bg-card)] px-4 py-4 sticky top-0 z-10 safe-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/mobile/dashboard')} className="text-[var(--m-text-secondary)] p-2">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-[var(--m-text)] font-bold flex items-center gap-2">
                <Clock className="h-5 w-5 text-[#ffd108]" />
                Timesheet
              </h1>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/mobile/dashboard')}
            className="text-[var(--m-text-secondary)] p-2"
            data-testid="home-btn"
          >
            <Home className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Week Navigation */}
      <div className="px-4 py-3 bg-[var(--m-bg-card)] flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigateWeek(-1)} className="text-[var(--m-text-secondary)]">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-[#ffd108]" />
          <span className="text-[var(--m-text)] font-medium">{formatWeekRange()}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigateWeek(1)} className="text-[var(--m-text-secondary)]">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Week Summary */}
      <div className="px-4 py-3 bg-[var(--m-bg-card)] flex items-center justify-between">
        <span className="text-[var(--m-text-secondary)] text-sm">Total Hours</span>
        <span className="text-[#ffd108] font-bold text-xl">{getTotalWeekHours()} hrs</span>
      </div>

      {/* Day Selector */}
      <div className="px-4 py-3">
        <div className="grid grid-cols-7 gap-1">
          {weekData?.entries.map((entry, idx) => {
            const status = getEntryStatus(entry);
            return (
              <button
                key={entry.date}
                onClick={() => selectDay(entry)}
                className={`p-2 rounded-lg text-center transition-colors ${
                  selectedDay?.date === entry.date
                    ? 'bg-[#ffd108] text-[#1a1a1a]'
                    : 'bg-[var(--m-bg-card)] text-[var(--m-text)] hover:bg-[var(--m-bg-elevated)]'
                }`}
              >
                <div className="text-xs font-medium">{dayNames[idx]}</div>
                <div className={`text-lg font-bold ${isToday(entry.date) ? 'underline' : ''}`}>
                  {formatDate(entry.date)}
                </div>
                <div className="h-1.5 w-1.5 rounded-full mx-auto mt-1" style={{
                  backgroundColor: 
                    status === 'approved' ? '#22c55e' :
                    status === 'submitted' ? '#f59e0b' :
                    status === 'filled' ? '#3b82f6' : 'transparent'
                }} />
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {selectedDay && (
          <>
            {/* Time Entry */}
            <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
              <CardHeader className="pb-2">
                <CardTitle className="text-[var(--m-text)] text-sm flex items-center justify-between">
                  <span>{formatDateLong(selectedDay.date, tenantSettings?.date_format)}</span>
                  {selectedDay.status === 'submitted' && (
                    <Badge className="bg-amber-900 hover:bg-amber-800 text-amber-300">Submitted</Badge>
                  )}
                  {selectedDay.status === 'approved' && (
                    <Badge className="bg-green-900 text-green-300">Approved</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[var(--m-text-secondary)] text-xs">Start Time</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <Select
                        value={editForm.start_time ? editForm.start_time.split(':')[0] : defaultStart.split(':')[0]}
                        onValueChange={(v) => updateTimePart('start_time', 'hour', v)}
                        disabled={selectedDay.status !== 'draft'}
                      >
                        <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]" data-testid="timesheet-start-hour">
                          <SelectValue placeholder="HH" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px] bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]">
                          {hours.map((h) => (
                            <SelectItem key={h} value={h}>{h}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={editForm.start_time ? editForm.start_time.split(':')[1] : '00'}
                        onValueChange={(v) => updateTimePart('start_time', 'minute', v)}
                        disabled={selectedDay.status !== 'draft'}
                      >
                        <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]" data-testid="timesheet-start-minute">
                          <SelectValue placeholder="MM" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px] bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]">
                          {minutes.map((m) => (
                            <SelectItem key={m} value={m}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[var(--m-text-secondary)] text-xs">Finish Time</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <Select
                        value={editForm.finish_time ? editForm.finish_time.split(':')[0] : defaultFinish.split(':')[0]}
                        onValueChange={(v) => updateTimePart('finish_time', 'hour', v)}
                        disabled={selectedDay.status !== 'draft'}
                      >
                        <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]" data-testid="timesheet-finish-hour">
                          <SelectValue placeholder="HH" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px] bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]">
                          {hours.map((h) => (
                            <SelectItem key={h} value={h}>{h}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={editForm.finish_time ? editForm.finish_time.split(':')[1] : defaultFinish.split(':')[1]}
                        onValueChange={(v) => updateTimePart('finish_time', 'minute', v)}
                        disabled={selectedDay.status !== 'draft'}
                      >
                        <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]" data-testid="timesheet-finish-minute">
                          <SelectValue placeholder="MM" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px] bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]">
                          {minutes.map((m) => (
                            <SelectItem key={m} value={m}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-[var(--m-text-secondary)] text-xs flex items-center gap-2">
                    <Coffee className="h-3 w-3" />
                    Lunch Break
                  </Label>
                  <Select
                    value={String(editForm.lunch_break_minutes)}
                    onValueChange={(v) => setEditForm({...editForm, lunch_break_minutes: parseInt(v)})}
                    disabled={selectedDay.status !== 'draft'}
                  >
                    <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">No Break</SelectItem>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Allocated Jobs (read-only) */}
                {jobsForDate.length > 0 && (
                  <div>
                    <Label className="text-[var(--m-text-secondary)] text-xs flex items-center gap-2">
                      <Briefcase className="h-3 w-3" />
                      Allocated Jobs
                    </Label>
                    <div className="mt-1 space-y-1">
                      {jobsForDate.map(j => (
                        <div key={j.id} className="bg-[var(--m-bg)] border border-[var(--m-border)] rounded-md px-3 py-2 text-sm text-[var(--m-text)]">
                          <span className="text-[#ffd108]">#{j.job_number}</span>
                          {j.customer_name ? ` — ${j.customer_name}` : ''}
                          {j.equipment_type ? <span className="text-[var(--m-text-secondary)] text-xs ml-1">({j.equipment_type})</span> : ''}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <Label className="text-[var(--m-text-secondary)] text-xs flex items-center gap-2">
                    <FileText className="h-3 w-3" />
                    Notes
                  </Label>
                  <textarea
                    value={editForm.notes}
                    onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                    disabled={selectedDay.status !== 'draft'}
                    placeholder="Optional notes..."
                    rows={2}
                    className="w-full bg-[var(--m-bg)] border border-[var(--m-border-light)] rounded-md text-[var(--m-text)] text-sm p-2 mt-1 resize-none placeholder:text-[var(--m-text-muted)] disabled:opacity-50"
                  />
                </div>

                {/* Time Blocks Section */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[var(--m-text-secondary)] text-xs flex items-center gap-2">
                      <Layers className="h-3 w-3" />
                      Time Blocks
                      {editForm.time_blocks.length > 0 && (
                        <Badge variant="outline" className="text-[10px] font-mono border-[var(--m-border)]">
                          {editForm.time_blocks.length} &bull; {getBlocksTotal()}h
                        </Badge>
                      )}
                    </Label>
                    {selectedDay.status === 'draft' && (
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs border-[var(--m-border)] text-[var(--m-text-secondary)]"
                          onClick={handleAutoFillBlocks}
                          disabled={autoFillLoading}
                          data-testid="mobile-autofill-blocks-btn"
                        >
                          {autoFillLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3 mr-1" />}
                          Auto-fill
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs border-[var(--m-border)] text-[var(--m-text-secondary)]"
                          onClick={addBlock}
                          data-testid="mobile-add-block-btn"
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {editForm.time_blocks.length > 0 ? (
                    <div className="space-y-2">
                      {mobileOverlaps.msgs.length > 0 && (
                        <div className="rounded-md border border-red-500/40 bg-red-900/10 px-3 py-2" data-testid="mobile-overlap-warning">
                          <div className="flex items-center gap-1.5 text-red-400 text-xs font-semibold">
                            <AlertTriangle className="h-3 w-3" />
                            Overlapping Blocks
                          </div>
                          {mobileOverlaps.msgs.map((m, i) => (
                            <p key={i} className="text-[10px] text-red-400/70 pl-4">{m}</p>
                          ))}
                        </div>
                      )}
                      {editForm.time_blocks.map((block, idx) => (
                        <div
                          key={block.block_id || idx}
                          className={`rounded-lg border p-3 ${
                            mobileOverlaps.set.has(idx)
                              ? 'border-red-500/50 bg-red-900/10 ring-1 ring-red-500/20'
                              : block.is_unallocated
                                ? 'border-dashed border-amber-500/40 bg-amber-900/10'
                                : 'border-[var(--m-border)] bg-[var(--m-bg)]'
                          }`}
                          data-testid={`mobile-block-${idx}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {mobileOverlaps.set.has(idx) && (
                                <AlertTriangle className="h-3 w-3 text-red-400 shrink-0" />
                              )}
                              {block.is_unallocated ? (
                                <Badge className="bg-amber-900/50 hover:bg-amber-900/70 text-amber-300 text-[10px]">Unalloc</Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] border-[var(--m-border)]">
                                  {block.job_number ? `#${block.job_number}` : 'Job'}
                                </Badge>
                              )}
                              {block.customer_name && (
                                <span className="text-[10px] text-[var(--m-text-secondary)] truncate max-w-[100px]">{block.customer_name}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-[var(--m-text)]">
                                {block.start_time}-{block.end_time}
                              </span>
                              <span className="text-[10px] text-[var(--m-text-secondary)]">
                                ({calcBlockHrs(block.start_time, block.end_time, block.break_minutes || 0).toFixed(1)}h)
                              </span>
                              {selectedDay.status === 'draft' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-red-400 hover:text-red-300"
                                  onClick={() => removeBlock(idx)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {block.notes && (
                            <p className="text-[10px] text-[var(--m-text-muted)] mt-1">{block.notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-3 text-[10px] text-[var(--m-text-muted)] border border-dashed border-[var(--m-border)] rounded-lg">
                      No time blocks. Tap Auto-fill to generate from dockets.
                    </div>
                  )}
                </div>

                {/* Calculated Hours */}
                <div className="bg-[var(--m-bg)] rounded-lg p-4 text-center">
                  <p className="text-[var(--m-text-secondary)] text-xs mb-1">Total Hours</p>
                  <p className="text-[#ffd108] text-3xl font-bold">{calculateHours()}</p>
                </div>

                {selectedDay.status === 'draft' && (
                  <Button 
                    className="w-full btn-volt" 
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Save Entry
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Legend */}
            <div className="flex items-center justify-center gap-4 text-xs text-[var(--m-text-secondary)]">
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-blue-500" />
                <span>Saved</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-amber-500" />
                <span>Submitted</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-green-500" />
                <span>Approved</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Submit Week — inline at end of timesheet */}
      <div className="px-4 pt-2">
        <Button
          variant="outline"
          className="w-full h-12 border-[#ffd108] text-[#ffd108] hover:bg-[#ffd108] hover:text-[#1a1a1a]"
          onClick={handleSubmitWeek}
          data-testid="submit-week-btn"
        >
          <Send className="h-4 w-4 mr-2" />
          Submit Week for Approval
        </Button>
      </div>
    </div>
    </MobileLayout>
  );
};

export default MobileTimesheet;

